import React, { useState, useEffect } from 'react';
import { normalizeTeamName, getTeamLogo } from '../utils/teamUtils';
import { calculatePlayerPerformanceScore } from '../utils/playerUtils';

// Add these helper functions
const formatMatchupDate = (date) => {
  if (!date) return '';
  // Split on space to separate date and time
  const [datePart, timePart] = date.split(' ');
  // Format as "DD MMM HH:MM"
  const [day, month, year] = datePart.split('/');
  const formattedDate = `${day} ${getMonthAbbr(month)} ${timePart}`;
  return formattedDate;
};

const getMonthAbbr = (month) => {
  const months = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return months[parseInt(month)];
};

// Move getDefaultRound function here, before the component
const getDefaultRound = (fixtures) => {
  if (!fixtures || fixtures.length === 0) return 'Round 1';
  
  const today = new Date();
  
  // Find the first fixture that hasn't been played yet
  const upcomingFixture = fixtures.find(fixture => {
    if (!fixture.Date) return false;
    
    const [datePart, timePart] = fixture.Date.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hours, minutes] = timePart.split(':');
    
    const fixtureDate = new Date(year, month - 1, day, hours, minutes);
    return fixtureDate >= today;
  });
  
  if (upcomingFixture) {
    return `Round ${upcomingFixture['Round Number']}`;
  }
  
  // If no upcoming fixtures, get the last regular round
  const lastRegularRound = fixtures.reduce((lastRound, fixture) => {
    const roundNum = fixture['Round Number'];
    if (
      roundNum && 
      !isNaN(parseInt(roundNum)) && 
      !roundNum.includes('Finals') && 
      !roundNum.includes('Grand')
    ) {
      return Math.max(lastRound, parseInt(roundNum));
    }
    return lastRound;
  }, 1);
  
  return `Round ${lastRegularRound}`;
};

const Matchups = ({ players, fixtures, isDarkMode }) => {
  // Pass fixtures to getDefaultRound
  const [round, setRound] = useState(() => getDefaultRound(fixtures));
  const [matchups, setMatchups] = useState([]);
  const [selectedMatchup, setSelectedMatchup] = useState(null);
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [displayStats, setDisplayStats] = useState([
    { key: 'points', label: 'PTS', className: 'w-14' },
    { key: 'tries', label: 'T', className: 'w-14' },
    { key: 'all_run_metres', label: 'M', className: 'w-14' },
    { key: 'tackles_made', label: 'TK', className: 'w-14' },
    { key: 'performance_score', label: 'PP', className: 'w-16' }
  ]);
  const [sortConfig, setSortConfig] = useState({
    key: 'performance_score',
    direction: 'desc',
    table: null // 'home' or 'away'
  });

  // Add state for dropdown
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Add new state variables at the top with other states
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [showGameLogs, setShowGameLogs] = useState(false);
  const [modalType, setModalType] = useState(null); // 'venue' or 'opponent'

  // Get rounds directly from fixtures
  const getRounds = () => {
    if (!fixtures || fixtures.length === 0) return [];
    return [...new Set(fixtures.map(f => `Round ${f['Round Number']}`))].filter(round => {
      // Exclude any rounds that don't start with "Round " or contain "Finals" or "Grand"
      return round.startsWith('Round ') && 
             !round.includes('Finals') && 
             !round.includes('Grand') &&
             // Convert to number to ensure it's a regular round
             !isNaN(parseInt(round.replace('Round ', '')));
    }).sort((a, b) => {
      const aNum = parseInt(a.match(/\d+/)?.[0] || 0);
      const bNum = parseInt(b.match(/\d+/)?.[0] || 0);
      return aNum - bNum;
    });
  };

  // Update stats to match the NRL data structure
  useEffect(() => {
    // Update display stats based on the actual data structure
    if (players && players.length > 0) {
      const samplePlayer = players[0];
      const updatedStats = [
        { key: 'points', label: 'PTS', className: 'w-14' },
        { key: 'tries', label: 'T', className: 'w-14' },
        { key: 'all_run_metres', label: 'M', className: 'w-14' },
        { key: 'tackles_made', label: 'TK', className: 'w-14' },
        { key: 'performance_score', label: 'PP', className: 'w-16' }
      ];
      
      // Make sure the keys exist in the data
      const validStats = updatedStats.filter(stat => 
        stat.key === 'performance_score' || 
        samplePlayer.hasOwnProperty(stat.key)
      );
      
      // Replace the default stats
      setDisplayStats(validStats);
    }
  }, [players]);

  // Load matchups data when round changes
  useEffect(() => {
    if (players && players.length > 0) {
      loadMatchups();
    }
  }, [players, round]);

  // Load data for the selected matchup
  useEffect(() => {
    if (selectedMatchup) {
      loadMatchupDetails(selectedMatchup);
    }
  }, [selectedMatchup]);

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.round-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMatchups = () => {
    setLoading(true);
    
    try {
      // Filter fixtures by round
      const filteredFixtures = fixtures.filter(fixture => {
        const fixtureRound = `Round ${fixture['Round Number']}`;
        return fixtureRound === round;
      });
      
      // Convert fixtures to matchups format
      const matchupList = filteredFixtures.map(fixture => ({
        homeTeam: fixture['Home Team'],
        awayTeam: fixture['Away Team'],
        venue: fixture.Location,
        round: `Round ${fixture['Round Number']}`,
        date: fixture.Date
      }));
      
      setMatchups(matchupList);
      
      // Always set the first matchup when loading new matchups
      if (matchupList.length > 0) {
        setSelectedMatchup({
          homeTeam: matchupList[0].homeTeam,
          awayTeam: matchupList[0].awayTeam
        });
        setVenue(matchupList[0].venue);
      } else {
        // Clear selection if no matchups available
        setSelectedMatchup(null);
        setVenue(null);
      }
      
    } catch (error) {
      console.error("Error loading matchups:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMatchupDetails = (matchup) => {
    setLoading(true);
    
    try {
      // Get the venue from the matchups list
      const matchupInfo = matchups.find(
        m => m.homeTeam === matchup.homeTeam && m.awayTeam === matchup.awayTeam
      );
      
      if (matchupInfo) {
        setVenue(matchupInfo.venue);
      }
      
    } catch (error) {
      console.error("Error loading matchup details:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get players for a specific team in the selected matchup
  const getTeamPlayers = (teamName) => {
    if (!selectedMatchup) return [];
    
    const currentYear = 2025; // Set current year to 2025
    
    // Create a Map to store unique current players by player_name
    const currentPlayersMap = new Map();
    
    // Get all players for this team in 2025
    const currentTeamPlayers = players
      .filter(player => 
        // Only include players from 2025
        player.year === currentYear &&
        // Only include players currently on this team
        player.player_team === teamName
      )
      .map(player => ({
        ...player,
        // For players with no stats yet, initialize with zeros
        points: player.points || 0,
        tries: player.tries || 0,
        all_run_metres: player.all_run_metres || 0,
        tackles_made: player.tackles_made || 0,
        performance_score: calculatePlayerPerformanceScore(player) || 0
      }));

    // Use Map to store unique players, keeping the most recent appearance
    currentTeamPlayers.forEach(player => {
      const existingPlayer = currentPlayersMap.get(player.player_name);
      if (!existingPlayer || (player.round && (!existingPlayer.round || player.round > existingPlayer.round))) {
        currentPlayersMap.set(player.player_name, player);
      }
    });

    // Convert Map values to array and sort
    return Array.from(currentPlayersMap.values())
      .sort((a, b) => {
        // Sort by position number if available
        if (a.position_number && b.position_number) {
          return parseInt(a.position_number) - parseInt(b.position_number);
        }
        return a.player_name.localeCompare(b.player_name);
      });
  };

  // Update the formatStatValue function
  const formatStatValue = (stat, value) => {
    if (value === undefined || value === null) return '-';
    
    // Always format to 1 decimal place
    return parseFloat(value).toFixed(1);
  };

  // Get CSS class for stat value cell
  const getStatValueClass = (stat, value) => {
    if (value === undefined || value === null) return "";
    
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "";
    
    // Different thresholds for different stats
    if (stat.key === 'performance_score') {
      if (numValue >= 80) return "text-green-600 dark:text-green-400 font-medium";
      if (numValue >= 60) return "text-lime-600 dark:text-lime-400";
      if (numValue >= 40) return "text-yellow-600 dark:text-yellow-400";
      if (numValue >= 20) return "text-orange-600 dark:text-orange-400";
      return "text-red-600 dark:text-red-400";
    } else if (stat.key === 'all_run_metres') {
      if (numValue >= 150) return "text-green-600 dark:text-green-400 font-medium";
      if (numValue >= 100) return "text-lime-600 dark:text-lime-400";
      if (numValue >= 50) return "text-yellow-600 dark:text-yellow-400";
      if (numValue >= 20) return "text-orange-600 dark:text-orange-400";
      return "text-red-600 dark:text-red-400";
    } else if (stat.key === 'tackles_made') {
      if (numValue >= 40) return "text-green-600 dark:text-green-400 font-medium";
      if (numValue >= 30) return "text-lime-600 dark:text-lime-400";
      if (numValue >= 20) return "text-yellow-600 dark:text-yellow-400";
      if (numValue >= 10) return "text-orange-600 dark:text-orange-400";
      return "text-red-600 dark:text-red-400";
    } else if (stat.key === 'tries') {
      if (numValue >= 2) return "text-green-600 dark:text-green-400 font-medium";
      if (numValue >= 1) return "text-lime-600 dark:text-lime-400";
      return "text-gray-600 dark:text-gray-400";
    } else {
      if (numValue >= 20) return "text-green-600 dark:text-green-400 font-medium";
      if (numValue >= 15) return "text-lime-600 dark:text-lime-400";
      if (numValue >= 10) return "text-yellow-600 dark:text-yellow-400";
      if (numValue >= 5) return "text-orange-600 dark:text-orange-400";
      return "text-red-600 dark:text-red-400";
    }
  };

  // Get player's history against this opponent with averages
  const getPlayerVsOpponent = (player) => {
    const opponentTeam = player.player_team === selectedMatchup.homeTeam 
      ? selectedMatchup.awayTeam 
      : selectedMatchup.homeTeam;
      
    // Get all games for this player regardless of team
    const allGames = players.filter(p => 
      p.player_name === player.player_name && // Include all games for this player
      p.player_team === player.player_team    // Only include games for their current team
    );
    
    // Get games against this opponent (where player was on their current team)
    const gamesVsOpponent = allGames.filter(p => 
      (p.home_team === opponentTeam && !p.is_home_game) || // Player's team was away vs opponent
      (p.away_team === opponentTeam && p.is_home_game)     // Player's team was home vs opponent
    );

    if (gamesVsOpponent.length === 0) return null;

    // Calculate averages
    const calculateAvg = (games, stat) => {
      if (!games.length) return 0;
      return games.reduce((sum, p) => sum + (parseFloat(p[stat]) || 0), 0) / games.length;
    };

    return {
      seasonAvg: {
        points: calculateAvg(allGames, 'points'),
        tries: calculateAvg(allGames, 'tries'),
        all_run_metres: calculateAvg(allGames, 'all_run_metres'),
        tackles_made: calculateAvg(allGames, 'tackles_made'),
        performance_score: allGames.reduce((sum, p) => sum + calculatePlayerPerformanceScore(p), 0) / allGames.length
      },
      vsOpponent: {
        points: calculateAvg(gamesVsOpponent, 'points'),
        tries: calculateAvg(gamesVsOpponent, 'tries'),
        all_run_metres: calculateAvg(gamesVsOpponent, 'all_run_metres'),
        tackles_made: calculateAvg(gamesVsOpponent, 'tackles_made'),
        performance_score: gamesVsOpponent.reduce((sum, p) => sum + calculatePlayerPerformanceScore(p), 0) / gamesVsOpponent.length
      },
      gamesVsOpponent: gamesVsOpponent.length
    };
  };

  // Get player's stats at venue with averages
  const getPlayerAtVenue = (player) => {
    if (!venue) return null;
    
    // Get all games for this player regardless of team
    const allGames = players.filter(p => 
      p.player_name === player.player_name  // Include all games for this player
    );
    
    // Get games at this venue
    const gamesAtVenue = allGames.filter(p => p.venue === venue);

    if (gamesAtVenue.length === 0) return null;

    // Calculate averages
    const calculateAvg = (games, stat) => {
      if (!games.length) return 0;
      return games.reduce((sum, p) => sum + (parseFloat(p[stat]) || 0), 0) / games.length;
    };

    return {
      seasonAvg: {
        points: calculateAvg(allGames, 'points'),
        tries: calculateAvg(allGames, 'tries'),
        all_run_metres: calculateAvg(allGames, 'all_run_metres'),
        tackles_made: calculateAvg(allGames, 'tackles_made'),
        performance_score: allGames.reduce((sum, p) => sum + calculatePlayerPerformanceScore(p), 0) / allGames.length
      },
      atVenue: {
        points: calculateAvg(gamesAtVenue, 'points'),
        tries: calculateAvg(gamesAtVenue, 'tries'),
        all_run_metres: calculateAvg(gamesAtVenue, 'all_run_metres'),
        tackles_made: calculateAvg(gamesAtVenue, 'tackles_made'),
        performance_score: gamesAtVenue.reduce((sum, p) => sum + calculatePlayerPerformanceScore(p), 0) / gamesAtVenue.length
      },
      gamesAtVenue: gamesAtVenue.length
    };
  };

  // Update the renderPlayerStats function's stat rendering
  const renderPlayerStats = (player, type = 'opponent') => {
    const stats = type === 'opponent' ? getPlayerVsOpponent(player) : getPlayerAtVenue(player);
    if (!stats) return null;

    const compareStats = type === 'opponent' ? stats.vsOpponent : stats.atVenue;
    const seasonAvg = stats.seasonAvg;
    const gamesCount = type === 'opponent' ? stats.gamesVsOpponent : stats.gamesAtVenue;

    const opponentTeam = player.player_team === selectedMatchup.homeTeam 
      ? selectedMatchup.awayTeam 
      : selectedMatchup.homeTeam;

    const playerWithStats = {
      ...player,
      ...compareStats
    };

    return (
      <tr 
        className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
        onClick={() => {
          setSelectedPlayer(player);
          setModalType(type); // Set the modal type when clicking
          setShowGameLogs(true);
        }}
      >
        <td className="px-2 py-1.5 text-gray-900 dark:text-white">
          {player.player_name}
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {gamesCount} {type === 'opponent' ? `vs ${normalizeTeamName(opponentTeam)}` : 'at venue'}
          </div>
        </td>
        {displayStats.map(stat => {
          const comparisonValue = compareStats[stat.key];
          const avgValue = seasonAvg[stat.key];
          const diff = comparisonValue - avgValue;
          const diffClass = diff > 0 ? 'text-green-600 dark:text-green-400' : 
                           diff < 0 ? 'text-red-600 dark:text-red-400' : 
                           'text-gray-600 dark:text-gray-400';

          return (
            <td key={stat.key} className="px-2 py-1.5 text-center">
              <span className={diffClass}>{formatStatValue(stat, comparisonValue)}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400 block">
                ({formatStatValue(stat, avgValue)})
              </span>
            </td>
          );
        })}
      </tr>
    );
  };

  const rounds = getRounds();
  const homeTeamPlayers = selectedMatchup ? getTeamPlayers(selectedMatchup.homeTeam) : [];
  const awayTeamPlayers = selectedMatchup ? getTeamPlayers(selectedMatchup.awayTeam) : [];

  // Update the sorting function
  const sortPlayers = (players, sortKey) => {
    return [...players].sort((a, b) => {
      let aValue, bValue;

      // Handle different types of values
      if (sortKey === 'player_name') {
        aValue = a[sortKey];
        bValue = b[sortKey];
      } else {
        // For all numeric stats including performance_score, use the values directly
        // since they're already calculated and added to the player object
        aValue = parseFloat(a[sortKey]) || 0;
        bValue = parseFloat(b[sortKey]) || 0;
      }

      // For stats comparison
      if (sortKey !== 'player_name') {
        if (sortConfig.direction === 'asc') {
          return aValue - bValue;
        }
        return bValue - aValue;
      }

      // For player name comparison
      if (sortConfig.direction === 'asc') {
        return aValue.localeCompare(bValue);
      }
      return bValue.localeCompare(aValue);
    });
  };

  // Add this function to handle sort clicks
  const requestSort = (key, table) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
      table
    }));
  };

  // Add this function to get sort indicator
  const getSortIndicator = (key, table) => {
    if (sortConfig.table !== table || sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // Update the GameLogsModal component
  const GameLogsModal = ({ player, opponent, venue, onClose }) => {
    const relevantGames = players.filter(p => {
      // Base condition: same player name and current team
      const isCurrentPlayer = p.player_name === player.player_name && 
                            p.player_team === player.player_team;
      
      if (!isCurrentPlayer) return false;

      if (opponent) {
        // For opponent games, check if played against them (not for them)
        return (p.home_team === opponent && !p.is_home_game) || 
               (p.away_team === opponent && p.is_home_game);
      } else if (venue) {
        // For venue games, just check the venue
        return p.venue === venue;
      }
      return false;
    }).sort((a, b) => {
      // Sort by year first, then round number
      if (a.year !== b.year) {
        return b.year - a.year; // Most recent year first
      }
      const aNum = parseInt(a.round.replace('Round ', ''));
      const bNum = parseInt(b.round.replace('Round ', ''));
      return bNum - aNum; // Most recent round first
    });

    // Helper function to format stats without decimals
    const formatModalStat = (value) => {
      if (value === undefined || value === null) return '-';
      return Math.round(parseFloat(value));
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {player.player_name} {opponent ? `vs ${opponent}` : `at ${venue}`}
            </h3>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              ✕
            </button>
          </div>
          <div className="overflow-x-auto">
            <div className="overflow-y-auto max-h-[calc(80vh-8rem)]">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Year</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Round</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">PTS</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">T</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">M</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">TK</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">PP</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {relevantGames.map((game, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{game.year}</td>
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">{game.round.replace('Round ', '')}</td>
                      <td className="px-4 py-2 text-sm text-center text-gray-900 dark:text-white">{formatModalStat(game.points)}</td>
                      <td className="px-4 py-2 text-sm text-center text-gray-900 dark:text-white">{formatModalStat(game.tries)}</td>
                      <td className="px-4 py-2 text-sm text-center text-gray-900 dark:text-white">{formatModalStat(game.all_run_metres)}</td>
                      <td className="px-4 py-2 text-sm text-center text-gray-900 dark:text-white">{formatModalStat(game.tackles_made)}</td>
                      <td className="px-4 py-2 text-sm text-center text-gray-900 dark:text-white">
                        {formatModalStat(calculatePlayerPerformanceScore(game))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-2 md:p-4 h-full overflow-auto">      
      {/* Combined Round and Matchup Selector Row */}
      <div className="mb-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {/* Round dropdown */}
          <div className="relative inline-block round-dropdown">
            <button 
              className="px-2 py-1 md:px-3 md:py-2 text-xs md:text-sm rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 flex items-center"
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            >
              <span>{round}</span>
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            {isDropdownOpen && (
              <div className="absolute z-50 mt-1 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                <div className="max-h-60 overflow-y-auto">
                  {getRounds().map(r => (
                    <button
                      key={r}
                      className={`w-full text-left px-3 py-2 text-xs md:text-sm ${
                        round === r
                          ? 'bg-accent text-white dark:bg-accent-light dark:text-gray-900'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() => {
                        setRound(r);
                        setIsDropdownOpen(false);
                      }}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Matchup selection - now in the same row as round selector */}
          {matchups.length > 0 && (
            <div className="flex-1 overflow-x-auto">
              <div className="flex gap-2 pb-1">
                {matchups.map((matchup, index) => (
                  <button
                    key={index}
                    className={`flex-shrink-0 p-2 rounded-md flex flex-col items-center ${
                      selectedMatchup && selectedMatchup.homeTeam === matchup.homeTeam && selectedMatchup.awayTeam === matchup.awayTeam
                        ? 'bg-accent text-white dark:bg-accent-light dark:text-gray-900' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                    onClick={() => setSelectedMatchup({
                      homeTeam: matchup.homeTeam,
                      awayTeam: matchup.awayTeam
                    })}
                  >
                    <div className="flex items-center">
                      <img 
                        src={getTeamLogo(matchup.homeTeam)} 
                        alt={matchup.homeTeam} 
                        className="w-5 h-5"
                      />
                      <span className="mx-1 text-xs">vs</span>
                      <img 
                        src={getTeamLogo(matchup.awayTeam)} 
                        alt={matchup.awayTeam} 
                        className="w-5 h-5"
                      />
                    </div>
                    <div className="text-[10px] mt-1 text-center">
                      {formatMatchupDate(matchup.date)}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-accent dark:border-accent-light"></div>
        </div>
      ) : selectedMatchup ? (
        <div className="space-y-4">
          {/* vs Opponent Section */}
          <div>
            <h2 className="text-sm md:text-base font-bold text-gray-800 dark:text-white px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-t-md border-b border-gray-200 dark:border-gray-600 mb-2">
              vs Opponent
            </h2>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Home team */}
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-2 mb-2 px-2">
                  <img 
                    src={getTeamLogo(selectedMatchup.homeTeam)} 
                    alt={selectedMatchup.homeTeam} 
                    className="w-6 h-6"
                  />
                  <h3 className="text-sm md:text-base font-bold text-gray-800 dark:text-white">
                    {normalizeTeamName(selectedMatchup.homeTeam)}
                  </h3>
                </div>
                
                {/* Player stats table */}
                <div className="overflow-x-auto">
                  <div className="max-h-[350px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                      <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th 
                            className="px-2 py-1.5 md:py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => requestSort('player_name', 'home')}
                          >
                            <div className="flex items-center">
                              <span className="mr-1">Player</span> 
                              {getSortIndicator('player_name', 'home')}
                            </div>
                          </th>
                          {displayStats.map(stat => (
                            <th 
                              key={stat.key}
                              className={`px-1 py-1.5 md:py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${stat.className || ''}`}
                              onClick={() => requestSort(stat.key, 'home')}
                            >
                              <div className="flex items-center justify-center">
                                <span className="mr-1">{stat.label}</span>
                                {getSortIndicator(stat.key, 'home')}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortPlayers(
                          homeTeamPlayers.map(player => {
                            const stats = getPlayerVsOpponent(player);
                            return {
                              ...player,
                              ...(stats ? stats.vsOpponent : {})
                            };
                          }),
                          sortConfig.key
                        ).map((player, index) => renderPlayerStats(player, 'opponent'))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Away team */}
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-2 mb-2 px-2">
                  <img 
                    src={getTeamLogo(selectedMatchup.awayTeam)} 
                    alt={selectedMatchup.awayTeam} 
                    className="w-6 h-6"
                  />
                  <h3 className="text-sm md:text-base font-bold text-gray-800 dark:text-white">
                    {normalizeTeamName(selectedMatchup.awayTeam)}
                  </h3>
                </div>
                
                {/* Player stats table */}
                <div className="overflow-x-auto">
                  <div className="max-h-[350px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                      <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th 
                            className="px-2 py-1.5 md:py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => requestSort('player_name', 'away')}
                          >
                            <div className="flex items-center">
                              <span className="mr-1">Player</span>
                              {getSortIndicator('player_name', 'away')}
                            </div>
                          </th>
                          {displayStats.map(stat => (
                            <th 
                              key={stat.key}
                              className={`px-1 py-1.5 md:py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${stat.className || ''}`}
                              onClick={() => requestSort(stat.key, 'away')}
                            >
                              <div className="flex items-center justify-center">
                                <span className="mr-1">{stat.label}</span>
                                {getSortIndicator(stat.key, 'away')}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortPlayers(
                          awayTeamPlayers.map(player => {
                            const stats = getPlayerVsOpponent(player);
                            return {
                              ...player,
                              ...(stats ? stats.vsOpponent : {})
                            };
                          }),
                          sortConfig.key
                        ).map((player, index) => renderPlayerStats(player, 'opponent'))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* at Venue Section - Restoring the venue section */}
          <div>
            <h2 className="text-sm md:text-base font-bold text-gray-800 dark:text-white px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-t-md border-b border-gray-200 dark:border-gray-600 mb-2">
              at {venue}
            </h2>
            <div className="flex flex-col md:flex-row gap-4">
              {/* Home team venue stats */}
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-2 mb-2 px-2">
                  <img 
                    src={getTeamLogo(selectedMatchup.homeTeam)} 
                    alt={selectedMatchup.homeTeam} 
                    className="w-6 h-6"
                  />
                  <h3 className="text-sm md:text-base font-bold text-gray-800 dark:text-white">
                    {normalizeTeamName(selectedMatchup.homeTeam)}
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <div className="max-h-[350px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                      <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th 
                            className="px-2 py-1.5 md:py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => requestSort('player_name', 'home')}
                          >
                            <div className="flex items-center">
                              <span className="mr-1">Player</span>
                              {getSortIndicator('player_name', 'home')}
                            </div>
                          </th>
                          {displayStats.map(stat => (
                            <th 
                              key={stat.key}
                              className={`px-1 py-1.5 md:py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${stat.className || ''}`}
                              onClick={() => requestSort(stat.key, 'home')}
                            >
                              <div className="flex items-center justify-center">
                                <span className="mr-1">{stat.label}</span>
                                {getSortIndicator(stat.key, 'home')}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortPlayers(
                          homeTeamPlayers.map(player => {
                            const stats = getPlayerAtVenue(player);
                            return {
                              ...player,
                              ...(stats ? stats.atVenue : {})
                            };
                          }),
                          sortConfig.key
                        ).map((player, index) => renderPlayerStats(player, 'venue'))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {/* Away team venue stats */}
              <div className="flex-1 overflow-hidden">
                <div className="flex items-center gap-2 mb-2 px-2">
                  <img 
                    src={getTeamLogo(selectedMatchup.awayTeam)} 
                    alt={selectedMatchup.awayTeam} 
                    className="w-6 h-6"
                  />
                  <h3 className="text-sm md:text-base font-bold text-gray-800 dark:text-white">
                    {normalizeTeamName(selectedMatchup.awayTeam)}
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <div className="max-h-[350px] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                      <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-900">
                        <tr>
                          <th 
                            className="px-2 py-1.5 md:py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800"
                            onClick={() => requestSort('player_name', 'away')}
                          >
                            <div className="flex items-center">
                              <span className="mr-1">Player</span>
                              {getSortIndicator('player_name', 'away')}
                            </div>
                          </th>
                          {displayStats.map(stat => (
                            <th 
                              key={stat.key}
                              className={`px-1 py-1.5 md:py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 ${stat.className || ''}`}
                              onClick={() => requestSort(stat.key, 'away')}
                            >
                              <div className="flex items-center justify-center">
                                <span className="mr-1">{stat.label}</span>
                                {getSortIndicator(stat.key, 'away')}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {sortPlayers(
                          awayTeamPlayers.map(player => {
                            const stats = getPlayerAtVenue(player);
                            return {
                              ...player,
                              ...(stats ? stats.atVenue : {})
                            };
                          }),
                          sortConfig.key
                        ).map((player, index) => renderPlayerStats(player, 'venue'))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-gray-500 dark:text-gray-400">
          <p className="text-lg mb-4">No matchups available for the selected filters</p>
        </div>
      )}
      
      {/* Modal */}
      {showGameLogs && selectedPlayer && (
        <GameLogsModal 
          player={selectedPlayer}
          opponent={modalType === 'opponent' ? (
            selectedPlayer.player_team === selectedMatchup.homeTeam ? 
              selectedMatchup.awayTeam : 
              selectedMatchup.homeTeam
          ) : null}
          venue={modalType === 'venue' ? venue : null}
          onClose={() => {
            setShowGameLogs(false);
            setSelectedPlayer(null);
            setModalType(null);
          }}
        />
      )}
    </div>
  );
};

export default Matchups; 